import { IPolyPolygon, pentagonal as pentagonalGrid } from "../grids/index.js";
import { RendererBase } from "../renderers/_base.js";
import { calcBearing, projectPoint, ptDistance } from "../common/plotting.js";
import { pentagonalBoard } from "../common/pentagons/index.js";
import { Pentagonal, PentagonalNodeData } from "../graphs/index.js";
import { attachBoardNearestClick, boardClickLocalPoint, BoardReturn, createGridlineLayers } from "./index.js";

export const pentagonal = (ctx: RendererBase): BoardReturn => {
    if ( (ctx.json === undefined) || (ctx.rootSvg === undefined) ) {
        throw new Error("Object in an invalid state!");
    }

    // Check required properties
    let boardsize: number|undefined;
    if ( (ctx.json.board === null) || ( (! ("width" in ctx.json.board)) && (! ("height" in ctx.json.board)) ) || ( (ctx.json.board.width === undefined) && (ctx.json.board.height === undefined) ) ) {
        throw new Error("Either the `width` or `height` property must be set for this board type.");
    }
    if ( ("width" in ctx.json.board) && ctx.json.board.width !== undefined ) {
        boardsize = ctx.json.board.width;
    } else if ( ("height" in ctx.json.board) && ctx.json.board.height !== undefined ) {
        boardsize = ctx.json.board.height;
    } else {
        throw new Error("Either the `width` or `height` property must be set for this board type.");
    }
    if ( (! ("style" in ctx.json.board)) || (ctx.json.board.style === undefined) ) {
        throw new Error("This function requires that a board style be defined.");
    }
    const style = ctx.json.board.style;

    let baseStroke = 1;
    let baseColour = ctx.options.colourContext.strokes;
    let baseOpacity = 1;
    if ( ("strokeWeight" in ctx.json.board) && (ctx.json.board.strokeWeight !== undefined) ) {
        baseStroke = ctx.json.board.strokeWeight;
    }
    if ( ("strokeColour" in ctx.json.board) && (ctx.json.board.strokeColour !== undefined) ) {
        baseColour = ctx.resolveColour(ctx.json.board.strokeColour) as string;
    }
    if ( ("strokeOpacity" in ctx.json.board) && (ctx.json.board.strokeOpacity !== undefined) ) {
        baseOpacity = ctx.json.board.strokeOpacity;
    }

    // Get a grid of points
    const g = pentagonalBoard(boardsize);
    const grid = pentagonalGrid({pentagonalGraph: g});
    const graph = new Pentagonal(g);
    const board = ctx.rootSvg.group().id("board");

    // Adjust base graph for Bluestone games
    if (style === "pentagonal-bluestone") {
        // delete any verts/edges relating to the tips
        const toDel = new Set<number>();
        const layer = g.layers[g.layers.length - 1];
        for (const side of layer) {
            toDel.add(side[0].id);
            toDel.add(side[side.length - 1].id);
        }
        for (const vid of toDel) {
            const found = graph.graph.findNode((key, attr) => (attr as PentagonalNodeData).id === vid);
            if (found === undefined) {
                throw new Error(`Could not find a tip point to delete.`);
            }
            graph.graph.dropNode(found);
        }

        // delete all edges between outer nodes
        for (const entry of graph.graph.edgeEntries()) {
            if ((entry.sourceAttributes as PentagonalNodeData).isOuter && (entry.targetAttributes as PentagonalNodeData).isOuter) {
                graph.graph.dropEdge(entry.edge);
            }
        }
    }

    const layers = createGridlineLayers(board);
    const gridlines = layers.root;
    ctx.markBoard({svgGroup: gridlines, preGridLines: true, grid});

    // Add board labels (skipping for now)
    // let labelColour = ctx.options.colourContext.labels;
    // if ( ("labelColour" in ctx.json.board) && (ctx.json.board.labelColour !== undefined) ) {
    //     labelColour = ctx.resolveColour(ctx.json.board.labelColour) as string;
    // }
    // let labelOpacity = 1;
    // if ( ("labelOpacity" in ctx.json.board) && (ctx.json.board.labelOpacity !== undefined) ) {
    //     labelOpacity = ctx.json.board.labelOpacity;
    // }
    // if ( (! ctx.json.options) || (! ctx.json.options.includes("hide-labels") ) ) {
    //     let hideHalf = false;
    //     if (ctx.json.options?.includes("hide-labels-half")) {
    //         hideHalf = true;
    //     }
    //     const labels = board.group().id("labels");
    //     let customLabels: string[]|undefined;
    //     if ( ("columnLabels" in ctx.json.board) && (ctx.json.board.columnLabels !== undefined) ) {
    //         customLabels = ctx.json.board.columnLabels;
    //     }
    //     let columnLabels = ctx.getLabels(customLabels, width);
    //     if ( (ctx.json.options !== undefined) && (ctx.json.options.includes("reverse-letters")) ) {
    //         columnLabels.reverse();
    //     }

    //     let rowLabels = ctx.getRowLabels(ctx.json.board.rowLabels, height);
    //     if ( (ctx.json.options !== undefined) && (ctx.json.options.includes("reverse-numbers")) ) {
    //         rowLabels.reverse();
    //     }

    //     if (ctx.json.options?.includes("swap-labels")) {
    //         const scratch = [...columnLabels];
    //         columnLabels = [...rowLabels];
    //         columnLabels.reverse();
    //         rowLabels = [...scratch];
    //         rowLabels.reverse();
    //     }

    //     // Columns (letters)
    //     for (let col = 0; col < width; col++) {
    //         const pointTop = {x: grid[0][col].x, y: grid[0][col].y - (cellsize) - (show.includes("N") ? bufferwidth : 0)};
    //         const pointBottom = {x: grid[height - 1][col].x, y: grid[height - 1][col].y + (cellsize) + (show.includes("S") ? bufferwidth : 0)};
    //         if (! hideHalf) {
    //             ctx.applyCoordinateLabelStyle(labels.text(columnLabels[col]), labelColour, labelOpacity).center(pointTop.x, pointTop.y);
    //         }
    //         ctx.applyCoordinateLabelStyle(labels.text(columnLabels[col]), labelColour, labelOpacity).center(pointBottom.x, pointBottom.y);
    //     }

    //     // Rows (numbers)
    //     for (let row = 0; row < height; row++) {
    //         const pointL = {x: grid[row][0].x - (cellsize) - (show.includes("W") ? bufferwidth : 0), y: grid[row][0].y};
    //         const pointR = {x: grid[row][width - 1].x + (cellsize) + (show.includes("E") ? bufferwidth : 0), y: grid[row][width - 1].y};
    //         ctx.applyCoordinateLabelStyle(labels.text(rowLabels[row]), labelColour, labelOpacity).center(pointL.x, pointL.y);
    //         if (! hideHalf) {
    //             ctx.applyCoordinateLabelStyle(labels.text(rowLabels[row]), labelColour, labelOpacity).center(pointR.x, pointR.y);
    //         }
    //     }
    // }

    // Draw grid lines
    // use graphs to determine connections and then draw each connection
    type Blocked = [{row: number;col: number;},...{row: number;col: number;}[]];
    let blocked: Blocked|undefined;
    if ( (ctx.json.board.blocked !== undefined) && (ctx.json.board.blocked !== null) && (Array.isArray(ctx.json.board.blocked)) && (ctx.json.board.blocked.length > 0) ){
        blocked = [...(ctx.json.board.blocked as Blocked)];
    }
    if (blocked !== undefined) {
        for (const entry of blocked) {
            graph.dropNode([entry.col, entry.row]);
        }
    }

    for (const entry of graph.graph.edgeEntries()) {
        const x1 = (entry.sourceAttributes as PentagonalNodeData).x;
        const y1 = (entry.sourceAttributes as PentagonalNodeData).y;
        const x2 = (entry.targetAttributes as PentagonalNodeData).x;
        const y2 = (entry.targetAttributes as PentagonalNodeData).y;
        layers.strokes.line(x1, y1, x2, y2).stroke({width: baseStroke, color: baseColour, opacity: baseOpacity, linecap: "round", linejoin: "round"}).attr({ 'pointer-events': 'none' });
    }

    const centrePt = grid[0][0];
    const outer = [...grid[grid.length - 1]];
    const boardFill: IPolyPolygon = {
        type: "poly",
        points: outer.map(p => {
            const bearing = calcBearing(centrePt.x, centrePt.y, p.x, p.y);
            const dist = ptDistance(centrePt.x, centrePt.y, p.x, p.y) + (ctx.cellsize / 1.5);
            const [nx, ny] = projectPoint(centrePt.x, centrePt.y, dist, bearing);
            return {x: nx, y: ny};
        })
    };

    ctx.markBoard({svgGroup: gridlines, preGridLines: false, grid});
    if (ctx.options.boardClick !== undefined) {
        const genericCatcher = ((e: { clientX: number; clientY: number; }) => {
            const clicked = boardClickLocalPoint(board, e.clientX, e.clientY);
            // simply find the point that's closest to the click
            const closest = {
                dist: Infinity,
                row: null as (null|number),
                col: null as (null|number),
            };
            for (let row = 0; row < grid.length; row++) {
                for (let col = 0; col < grid[row].length; col++) {
                    const pt = grid[row][col];
                    const dist = ptDistance(pt.x, pt.y, clicked.x, clicked.y);
                    if (dist < closest.dist) {
                        closest.dist = dist;
                        closest.row = row;
                        closest.col = col;
                    }
                }
            }
            if (closest.dist !== Infinity && closest.row !== null && closest.col !== null) {
                ctx.options.boardClick!(closest.row, closest.col, "");
            }
        });
        attachBoardNearestClick(board, genericCatcher, boardFill);
    }

    return {grid, boardFill};
}
